/* eslint-disable react/forbid-prop-types */
import { useMemo, useState } from 'react';
import { Card, Collapse, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {
  formatDate,
  getReservationsByProductType,
  price,
  sumOfGrossInCentsMinusCommission,
} from 'utils/helpers';
import { FORMAT_WEEKDAY_DATE_YEAR } from 'utils/helpers/datetime';
import { useCurrentBookingState } from 'utils/hooks/use_current_booking_state';
import LoadingSpinner from 'components/loading_spinner/loading_spinner';
import { deleteReservation } from 'redux/actions/reservation/delete';
import { getCurrentBooking } from 'redux/selectors/current_booking';
import { FerryViewNoData } from './ferry_view';

export const UnscheduledFerryBookingContent = ({
  reservations,
  onlyShowInfo,
  isLandingPage,
}) => {
  const dispatch = useDispatch();
  const [deletingReservations, setDeletingReservations] = useState([]);

  const customerFacingAdjustments = useMemo(() => {
    const groups = {};
    reservations.forEach((reservation) => {
      (reservation.adjustments_attributes || [])
        .filter((adj) => adj.display_to_customer)
        .forEach((adj) => {
          const key = adj.adjustment_category_id;
          if (!groups[key]) {
            groups[key] = {
              category_id: key,
              display_name: adj.display_name,
              total: 0,
            };
          }
          groups[key].total += adj.gross_in_cents;
        });
    });
    return Object.values(groups);
  }, [reservations]);

  const handleRemove = (bookingId, reservationId) => {
    setDeletingReservations((prev) => [...prev, reservationId]);
    dispatch(deleteReservation(bookingId, reservationId)).finally(() => {
      setDeletingReservations((prev) =>
        prev.filter((id) => id !== reservationId)
      );
    });
  };

  return (
    <Card.Body
      className={classnames('d-grid gap-3', {
        'px-0': isLandingPage,
      })}
    >
      {reservations.map((reservation) => {
        const {
          id,
          booking_id,
          gross_in_cents,
          pre_adjusted_gross_in_cents,
          pre_adjusted_commission_in_cents,
          quantity,
          selection_name,
          first_travel_date,
          description,
        } = reservation;
        const discount = pre_adjusted_gross_in_cents - gross_in_cents;
        const isDeleting = deletingReservations.includes(id);

        return (
          <div className="d-flex justify-content-between" key={id}>
            <div>
              <div className="d-flex align-items-center">
                <div className="fw-bold">
                  {`${quantity ? `${quantity} x` : ''} ${selection_name}`}
                </div>
                {description && !isLandingPage && (
                  <OverlayTrigger
                    placement="bottom"
                    trigger="click"
                    rootClose
                    overlay={<Tooltip>{description}</Tooltip>}
                  >
                    <i
                      role="button"
                      className="fas fa-question-circle icon__after-text text-primary"
                    />
                  </OverlayTrigger>
                )}
              </div>
              {discount > 0 && !isLandingPage && (
                <div className="text-success">
                  {price(discount, {
                    omitZeroCents: true,
                    fromCents: true,
                  })}{' '}
                  discount applied
                </div>
              )}
              <div
                className={classnames('text-small', {
                  'text-grey': !isLandingPage,
                })}
              >
                {formatDate(first_travel_date._value, FORMAT_WEEKDAY_DATE_YEAR)}
              </div>
            </div>
            <div className="text-end">
              {isDeleting ? (
                <LoadingSpinner
                  text="Deleting reservation..."
                  theme={classnames({ light: isLandingPage })}
                />
              ) : (
                <>
                  {!onlyShowInfo && (
                    <div
                      role="button"
                      onClick={() => handleRemove(booking_id, id)}
                      className={classnames(
                        'text-decoration-underline text-small',
                        { 'link-danger': !isLandingPage }
                      )}
                    >
                      Remove
                    </div>
                  )}
                  {!isLandingPage && (
                    <div className="fw-bold">
                      {price(gross_in_cents - pre_adjusted_commission_in_cents)}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        );
      })}
      {!isLandingPage &&
        customerFacingAdjustments.map((group) => (
          <div
            className="d-flex justify-content-between"
            key={group.category_id}
          >
            <div className="fw-bold">{group.display_name || 'Surcharge'}</div>
            <div className="fw-bold">{price(group.total)}</div>
          </div>
        ))}
    </Card.Body>
  );
};

UnscheduledFerryBookingContent.propTypes = {
  reservations: PropTypes.array,
  onlyShowInfo: PropTypes.bool,
  isLandingPage: PropTypes.bool,
};

const UnscheduledFerryBooking = () => {
  const [cardOpen, setCardOpen] = useState(true);
  const booking = useSelector(getCurrentBooking);
  const { isActive } = useCurrentBookingState();
  const ferryReservations = getReservationsByProductType(
    'unscheduledFerry',
    booking
  );

  const totalFerryPrice = sumOfGrossInCentsMinusCommission(ferryReservations);

  if (ferryReservations.length === 0) {
    return <FerryViewNoData />;
  }

  return (
    <Card>
      <Card.Header onClick={() => setCardOpen(!cardOpen)}>
        <div>Ferry</div>
        <div>
          {totalFerryPrice !== 0 && <span>{price(totalFerryPrice)}</span>}
          <i
            className={`fas ms-3 ${
              cardOpen ? 'fa-chevron-up' : 'fa-chevron-down'
            }`}
          />
        </div>
      </Card.Header>
      <Collapse in={cardOpen}>
        <div>
          <UnscheduledFerryBookingContent
            reservations={ferryReservations}
            onlyShowInfo={isActive}
          />
          {totalFerryPrice !== 0 && (
            <Card.Footer>
              <div className="d-flex justify-content-between w-100">
                <div className="left">
                  <div className="fw-bold">Subtotal</div>
                </div>
                <div className="right">
                  <div className="fw-bold">{price(totalFerryPrice)}</div>
                </div>
              </div>
            </Card.Footer>
          )}
        </div>
      </Collapse>
    </Card>
  );
};

export default UnscheduledFerryBooking;
